import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  cancelBulkImportAliProductsJob,
  getBulkImportAliProductsJob,
  listBulkImportAliProductsJobs,
  startBulkImportAliProductsJob,
} from "../../../../features/productSlice";
import API from "../../../../util/API";
import * as Icons from "lucide-react";

const MAX_BULK = 999;
const PRODUCT_GET_QPS = 300;
const DEFAULT_WORKERS = 300;
const MAX_WORKERS = 300;
const PRODUCT_CONCURRENCY = 12;
const JOB_ITEM_LIMIT = 999;
const STREAM_INTERVAL_MS = 1000;
const POLL_INTERVAL_MS = 1000;

const clsx = (...args) => args.filter(Boolean).join(" ");

function normalizeIds(input) {
  const raw = String(input || "")
    .split(/[\n, ]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  const ids = raw
    .map((x) => {
      const match = x.match(/item\/(\d+)\.html/i) || x.match(/(\d{10,})/);
      return match ? match[1] : "";
    })
    .filter(Boolean);

  const seen = new Set();
  const unique = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }
  return unique.slice(0, MAX_BULK);
}

function mapServerStatus(status) {
  if (status === "imported") return "success";
  if (status === "exists") return "exists";
  if (status === "failed") return "failed";
  if (status === "not_found") return "failed";
  if (status === "running") return "saving";
  if (status === "cancelled") return "cancelled";
  return "queued";
}

function toJobMessage(item) {
  const productId = String(item?.productId || "");
  const status = String(item?.status || "");
  if (status === "imported") {
    return `ID ${productId}: imported (${Number(item?.countriesSaved || 0)} countries).`;
  }
  if (status === "exists") return `ID ${productId}: already imported.`;
  if (status === "not_found") return `ID ${productId}: no valid AliExpress data found.`;
  if (status === "failed") return `ID ${productId}: failed - ${item?.error || item?.message || "Unknown error"}.`;
  if (status === "running") return `ID ${productId}: importing...`;
  if (status === "cancelled") return `ID ${productId}: cancelled.`;
  return "";
}

function formatBytes(value) {
  const bytes = Math.max(0, Number(value || 0));
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let next = bytes / 1024;
  let index = 0;
  while (next >= 1024 && index < units.length - 1) {
    next /= 1024;
    index += 1;
  }
  return `${next >= 10 ? next.toFixed(1) : next.toFixed(2)} ${units[index]}`;
}

function formatRate(value) {
  return `${formatBytes(value)}/s`;
}

function formatPauseMs(value) {
  const seconds = Math.max(0, Math.ceil(Number(value || 0) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return remaining ? `${minutes}m ${remaining}s` : `${minutes}m`;
}

function formatRunCap(value) {
  const n = Number(value || 0);
  return n > 0 ? String(n) : "All eligible";
}

function getStreamMeta(state) {
  const map = {
    idle: { label: "Idle", icon: "Circle", tone: "text-slate-600", pulse: false },
    connecting: { label: "Connecting", icon: "Wifi", tone: "text-amber-600", pulse: true },
    live: { label: "Live stream", icon: "Radio", tone: "text-emerald-600", pulse: true },
    polling: { label: "Polling fallback", icon: "RefreshCw", tone: "text-amber-600", pulse: true },
    closed: { label: "Closed", icon: "CheckCircle2", tone: "text-slate-600", pulse: false },
  };
  return map[state] || map.idle;
}

function buildApiUrl(path) {
  const base = String(API?.defaults?.baseURL || "").replace(/\/+$/, "");
  const normalizedPath = String(path || "").startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function StatCard({ label, value, icon, tone = "default" }) {
  const Icon = Icons?.[icon] || Icons?.Circle;
  const valueTone =
    tone === "brand"
      ? "text-[#D2042D]"
      : tone === "warn"
      ? "text-amber-600"
      : "text-slate-900";

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="p-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center">
          <Icon size={16} className="text-slate-500" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider truncate">
            {label}
          </p>
          <p className={`mt-1 text-lg font-semibold ${valueTone}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ state }) {
  const map = {
    queued: "bg-white border-slate-200 text-slate-700",
    saving: "bg-sky-50 border-sky-100 text-sky-700",
    success: "bg-emerald-50 border-emerald-100 text-emerald-700",
    exists: "bg-amber-50 border-amber-100 text-amber-700",
    failed: "bg-rose-50 border-rose-100 text-rose-700",
    cancelled: "bg-slate-50 border-slate-200 text-slate-700",
  };

  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
        map[state] || "bg-white border-slate-200 text-slate-700"
      )}>
      {state}
    </span>
  );
}

export default function AliExpressAdd() {
  const dispatch = useDispatch();

  const [bulkText, setBulkText] = useState("");
  const bulkIds = useMemo(() => normalizeIds(bulkText), [bulkText]);

  const [isOpen, setIsOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [activeJobId, setActiveJobId] = useState("");
  const [activeJobs, setActiveJobs] = useState([]);
  const [activeJobsLoading, setActiveJobsLoading] = useState(false);
  const [activeJobCancelId, setActiveJobCancelId] = useState("");
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [jobMetrics, setJobMetrics] = useState(null);
  const [jobSummary, setJobSummary] = useState(null);
  const [apiRateLimit, setApiRateLimit] = useState(null);
  const [stockCron, setStockCron] = useState(null);
  const [stockCronLoading, setStockCronLoading] = useState(false);
  const [streamState, setStreamState] = useState("idle");
  const [isHydratingJob, setIsHydratingJob] = useState(false);

  const pollTimerRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const streamAbortRef = useRef(null);
  const knownStateRef = useRef(new Map());
  const pauseLoggedRef = useRef(false);

  const stats = useMemo(() => {
    const summary = jobSummary || {};
    const hasSummary = Number(summary.total || 0) > 0;
    const total = hasSummary ? Number(summary.total || 0) : jobs.length;
    const done = hasSummary
      ? Number(summary.processed || 0)
      : jobs.filter((j) => ["success", "failed", "cancelled", "exists"].includes(j.state)).length;
    const ok = hasSummary ? Number(summary.imported || 0) : jobs.filter((j) => j.state === "success").length;
    const fail = hasSummary
      ? Number(summary.failed || 0) + Number(summary.notFound || 0)
      : jobs.filter((j) => j.state === "failed").length;
    const skipped = hasSummary ? Number(summary.exists || 0) : jobs.filter((j) => j.state === "exists").length;
    const running = jobs.filter((j) => j.state === "saving").length;
    const pct = total ? Math.round((done / total) * 100) : 0;
    return { total, done, ok, fail, skipped, running, pct };
  }, [jobSummary, jobs]);

  const transfer = useMemo(() => {
    const metrics = jobMetrics || {};
    return {
      apiRequests: Number(metrics.apiRequests || 0),
      apiResponses: Number(metrics.apiResponses || 0),
      requestBytes: Number(metrics.requestBytes || 0),
      responseBytes: Number(metrics.responseBytes || 0),
      totalBytes: Number(metrics.totalBytes || 0),
      bytesPerSecond: Number(metrics.bytesPerSecond || 0),
    };
  }, [jobMetrics]);

  const stockCronMeta = useMemo(() => {
    const status = stockCron?.status || {};
    const progress = status?.progress || null;
    const pausedByBulkImport = Boolean(stockCron?.pausedByBulkImport);
    const running = Boolean(stockCron?.running);
    const enabled = stockCron?.enabled === undefined ? true : Boolean(stockCron.enabled);
    const progressPercent = Number(progress?.progressPercent || 0);
    const processedRows = Number(progress?.processedRows || 0);
    const totalRows = Number(progress?.totalRows || 0);
    const rate = Number(progress?.concurrentPerSecond || stockCron?.concurrentPerSecond || PRODUCT_GET_QPS);
    const label = running
      ? "Running"
      : pausedByBulkImport
      ? "Paused"
      : enabled
      ? "Idle"
      : "Disabled";

    return {
      enabled,
      label,
      pausedByBulkImport,
      progressPercent: Math.min(100, Math.max(0, progressPercent)),
      processedRows,
      rate,
      runCapLabel: formatRunCap(progress?.maxProductsPerRun || stockCron?.maxProductsPerRun),
      running,
      statusState: String(status?.state || ""),
      tone: pausedByBulkImport ? "warn" : running ? "brand" : "default",
      totalRows,
    };
  }, [stockCron]);

  const streamMeta = getStreamMeta(streamState);
  const StreamIcon = Icons?.[streamMeta.icon] || Icons?.Circle;
  const apiWindowLimit = Number(
    apiRateLimit?.effectiveQps ||
      apiRateLimit?.adaptiveQps ||
      apiRateLimit?.configuredQps ||
      PRODUCT_GET_QPS
  );

  function pushLog(line) {
    setLogs((prev) => {
      const next = [...prev, `[${new Date().toLocaleTimeString()}] ${line}`];
      return next.slice(-300);
    });
  }

  const loadActiveBulkJobs = useCallback(async () => {
    setActiveJobsLoading(true);
    let action;
    try {
      action = await dispatch(listBulkImportAliProductsJobs({ limit: 10, status: "active" }));
    } catch (error) {
      action = { error: { message: error?.message || "Could not load active import jobs." } };
    } finally {
      setActiveJobsLoading(false);
    }

    if (action?.error) return;
    setActiveJobs(Array.isArray(action?.payload?.jobs) ? action.payload.jobs : []);
  }, [dispatch]);

  const loadStockCronStatus = useCallback(async () => {
    setStockCronLoading(true);
    try {
      const res = await API.get("/settings/aliexpress-stock/sync-config");
      setStockCron(res?.data || null);
    } catch {
      setStockCron(null);
    } finally {
      setStockCronLoading(false);
    }
  }, []);

  function initJobs(ids, state = "queued") {
    knownStateRef.current = new Map();
    pauseLoggedRef.current = false;
    setJobs(
      ids.map((id) => ({
        id,
        state,
        message: state === "saving" ? "Importing..." : "Queued",
        attempt: 0,
        startedAt: null,
        finishedAt: null,
      }))
    );
    setLogs([]);
    setJobMetrics(null);
    setJobSummary({
      exists: 0,
      failed: 0,
      imported: 0,
      notFound: 0,
      processed: 0,
      total: ids.length,
    });
    setApiRateLimit(null);
    setStreamState("idle");
    setIsCancelling(false);
    setIsHydratingJob(false);
  }

  function closeModalIfSafe() {
    if (isRunning) return;
    setIsOpen(false);
  }

  function clearPolling() {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  function clearStreaming() {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
    setStreamState("idle");
  }

  function applyJobSnapshot(job) {
    const items = Array.isArray(job?.items) ? job.items : [];
    setIsHydratingJob(false);
    setJobMetrics(job?.metrics || null);
    setJobSummary(job?.summary || null);
    const nextRateLimit = job?.rateLimit || null;
    setApiRateLimit(nextRateLimit);
    const isPaused = Boolean(nextRateLimit?.paused);
    if (isPaused && !pauseLoggedRef.current) {
      pushLog(
        `AliExpress product.get paused by ban/rate-limit detection; resumes in ${formatPauseMs(nextRateLimit?.pauseRemainingMs)}.`
      );
    }
    if (!isPaused && pauseLoggedRef.current) {
      pushLog("AliExpress product.get pause cleared.");
    }
    pauseLoggedRef.current = isPaused;

    const nextKnown = new Map(knownStateRef.current);

    const nextJobs = items.map((item) => {
      const id = String(item?.productId || "");
      const serverStatus = String(item?.status || "queued");
      const previousStatus = nextKnown.get(id);
      if (previousStatus !== serverStatus) {
        const line = toJobMessage(item);
        if (line) pushLog(line);
      }
      nextKnown.set(id, serverStatus);

      return {
        id,
        attempt: Number(item?.attempts || 0),
        finishedAt: item?.finishedAt || null,
        message: item?.message || item?.error || "Queued",
        startedAt: item?.startedAt || null,
        state: mapServerStatus(serverStatus),
      };
    });

    knownStateRef.current = nextKnown;
    setJobs(nextJobs);
  }

  function handleTerminalJob(job) {
    if (job.status === "completed") {
      clearPolling();
      setIsRunning(false);
      setIsCancelling(false);
      setStreamState("closed");
      pushLog("Bulk import completed.");
      loadActiveBulkJobs();
      loadStockCronStatus();
      toast.success(
        `Import completed. Imported ${job?.summary?.imported || 0}, skipped ${job?.summary?.exists || 0}, failed ${job?.summary?.failed || 0}.`
      );
      return true;
    }

    if (job.status === "failed" || job.status === "cancelled") {
      clearPolling();
      setIsRunning(false);
      setIsCancelling(false);
      setActiveJobCancelId("");
      setStreamState("closed");
      const msg = job?.error || `Import job ${job.status}.`;
      pushLog(`Bulk import ended with ${job.status}: ${msg}`);
      loadActiveBulkJobs();
      loadStockCronStatus();
      if (job.status === "cancelled") {
        toast.info(msg);
      } else {
        toast.error(msg);
      }
      return true;
    }

    return false;
  }

  async function startBulkJobStream(jobId) {
    if (!jobId) return;
    clearPolling();
    clearStreaming();

    const controller = new AbortController();
    streamAbortRef.current = controller;
    setStreamState("connecting");

    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        buildApiUrl(
          `/product/aliexpress/bulk/jobs/${jobId}/stream?itemLimit=${JOB_ITEM_LIMIT}&intervalMs=${STREAM_INTERVAL_MS}`
        ),
        {
          credentials: "include",
          headers: {
            Accept: "application/x-ndjson",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        }
      );

      if (!response.ok || !response.body) {
        throw new Error(`Stream failed with HTTP ${response.status}`);
      }

      setStreamState("live");
      pushLog("Live stream connected.");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.trim()) continue;
          const payload = JSON.parse(line);
          if (payload?.type === "error") throw new Error(payload.message || "Import stream failed.");
          const job = payload?.job;
          if (!job) continue;
          applyJobSnapshot(job);
          if (handleTerminalJob(job)) return;
        }
      }

      if (buffer.trim()) {
        const payload = JSON.parse(buffer);
        const job = payload?.job;
        if (job) {
          applyJobSnapshot(job);
          if (handleTerminalJob(job)) return;
        }
      }

      setStreamState("closed");
    } catch (error) {
      if (controller.signal.aborted) return;
      const msg = error?.message || "Live stream failed.";
      pushLog(`${msg} Falling back to polling.`);
      setStreamState("polling");
      startPolling(jobId);
    } finally {
      if (streamAbortRef.current === controller) {
        streamAbortRef.current = null;
      }
    }
  }

  async function pollBulkJob(jobId) {
    if (!jobId || pollInFlightRef.current) return;
    pollInFlightRef.current = true;

    let action;
    try {
      action = await dispatch(getBulkImportAliProductsJob({ itemLimit: JOB_ITEM_LIMIT, jobId }));
    } catch (error) {
      action = { error: { message: error?.message || "Could not fetch import progress." } };
    } finally {
      pollInFlightRef.current = false;
    }

    if (action?.error) {
      const msg = action.error.message || "Could not fetch import progress.";
      setIsHydratingJob(false);
      pushLog(`Progress polling failed: ${msg}`);
      return;
    }

    const job = action?.payload?.job;
    if (!job) {
      setIsHydratingJob(false);
      return;
    }
    applyJobSnapshot(job);
    handleTerminalJob(job);
  }

  function startPolling(jobId) {
    clearPolling();
    pollBulkJob(jobId);
    pollTimerRef.current = setInterval(() => {
      pollBulkJob(jobId);
    }, POLL_INTERVAL_MS);
  }

  async function runBackendBulkImport(ids) {
    setIsRunning(true);
    setIsCancelling(false);
    setIsOpen(true);
    setActiveJobId("");
    initJobs(ids, "queued");
    pushLog("Submitting bulk import job to backend...");

    let action;
    try {
      action = await dispatch(
        startBulkImportAliProductsJob({
          productIds: ids,
          workers: DEFAULT_WORKERS,
        })
      );
    } catch (error) {
      action = { error: { message: error?.message || "Bulk import job creation failed." } };
    }

    if (action?.error) {
      const msg = action.error.message || "Bulk import job creation failed.";
      const finishedAt = new Date().toISOString();
      pushLog(`Bulk import failed: ${msg}`);
      setJobs((prev) =>
        prev.map((j) => ({
          ...j,
          finishedAt,
          message: msg,
          state: "failed",
        }))
      );
      setIsRunning(false);
      setIsCancelling(false);
      return;
    }

    const job = action?.payload?.job;
    const jobId = String(job?._id || "");
    if (!jobId) {
      const finishedAt = new Date().toISOString();
      pushLog("Bulk import failed: missing job ID from backend.");
      setJobs((prev) =>
        prev.map((j) => ({
          ...j,
          finishedAt,
          message: "Missing job ID from backend.",
          state: "failed",
        }))
      );
      setIsRunning(false);
      setIsCancelling(false);
      return;
    }

    setActiveJobId(jobId);
    pushLog(`Job created: ${jobId}`);
    if (job) applyJobSnapshot(job);
    loadActiveBulkJobs();
    loadStockCronStatus();
    startBulkJobStream(jobId);
  }

  async function cancelBulkImport(jobId = activeJobId) {
    const targetJobId = String(jobId || "").trim();
    if (!targetJobId || isCancelling || activeJobCancelId) return;
    const confirmed = window.confirm(
      "Cancel this bulk import? The current product may finish, but queued products will stop."
    );
    if (!confirmed) return;

    setIsCancelling(true);
    setActiveJobCancelId(targetJobId);
    pushLog(`Cancellation requested for job ${targetJobId}...`);

    let action;
    try {
      action = await dispatch(cancelBulkImportAliProductsJob({ jobId: targetJobId }));
    } catch (error) {
      action = { error: { message: error?.message || "Cancel request failed." } };
    }

    if (action?.error) {
      const msg = action.error.message || "Cancel request failed.";
      pushLog(`Cancel request failed: ${msg}`);
      toast.error(msg);
      setIsCancelling(false);
      setActiveJobCancelId("");
      return;
    }

    const isCurrentModalJob = targetJobId === activeJobId;
    const job = action?.payload?.job;
    if (job && isCurrentModalJob) {
      applyJobSnapshot(job);
      handleTerminalJob(job);
    }
    await loadActiveBulkJobs();
    await loadStockCronStatus();
    setActiveJobCancelId("");
    if (!isCurrentModalJob) setIsCancelling(false);
    toast.info(action?.payload?.message || "Cancellation requested.");
  }

  function watchBulkImportJob(job) {
    const jobId = String(job?._id || "").trim();
    if (!jobId) return;
    clearPolling();
    clearStreaming();
    knownStateRef.current = new Map();
    pauseLoggedRef.current = false;
    setIsOpen(true);
    setIsRunning(!["completed", "failed", "cancelled"].includes(String(job?.status || "")));
    setActiveJobId(jobId);
    setJobs([]);
    setLogs([]);
    setJobMetrics(job?.metrics || null);
    setJobSummary(job?.summary || null);
    setApiRateLimit(job?.rateLimit || null);
    setStreamState("connecting");
    setIsHydratingJob(true);
    setIsCancelling(Boolean(job?.cancelRequested));
    pushLog(`Watching existing job: ${jobId}`);
    pollBulkJob(jobId);
    startBulkJobStream(jobId);
  }

  async function startBulkImport() {
    const ids = bulkIds;
    if (!ids.length) {
      toast.error("Please paste at least 1 valid AliExpress Product ID or URL.");
      return;
    }
    if (ids.length > MAX_BULK) {
      toast.error(`Maximum ${MAX_BULK} imports per run.`);
      return;
    }
    await runBackendBulkImport(ids);
  }

  useEffect(() => {
    loadActiveBulkJobs();
    loadStockCronStatus();
    const intervalId = setInterval(() => {
      loadActiveBulkJobs();
      loadStockCronStatus();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [loadActiveBulkJobs, loadStockCronStatus]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModalIfSafe();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      clearPolling();
      clearStreaming();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning]);

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 lg:grid-cols-7 gap-4" aria-label="Bulk import stats">
        <StatCard label="Detected" value={bulkIds.length} icon="ScanSearch" tone="brand" />
        <StatCard label="Running" value={stats.running} icon="Loader" />
        <StatCard label="Skipped" value={stats.skipped} icon="SkipForward" tone="warn" />
        <StatCard label="Completed" value={`${stats.done}/${stats.total || 0}`} icon="CheckCircle2" />
        <StatCard label="Bandwidth" value={formatBytes(transfer.totalBytes)} icon="Activity" />
        <StatCard label="Rate" value={formatRate(transfer.bytesPerSecond)} icon="Gauge" />
        <StatCard
          label="Stock cron"
          value={stockCronLoading && !stockCron ? "Loading" : stockCronMeta.label}
          icon={stockCronMeta.pausedByBulkImport ? "PauseCircle" : "Clock3"}
          tone={stockCronMeta.tone}
        />
      </section>

      {stockCronMeta.pausedByBulkImport ? (
        <section className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <div className="flex flex-wrap items-center gap-2">
            <Icons.PauseCircle size={16} aria-hidden="true" />
            <span className="font-semibold">Stock cron paused</span>
            <span>
              Bulk import is active. Cron resumes with {stockCronMeta.rate || PRODUCT_GET_QPS} req/sec after imports finish.
            </span>
          </div>
        </section>
      ) : null}

      <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Product IDs / URLs</h3>
            <p className="text-xs text-slate-500">
              Paste IDs or AliExpress URLs. Duplicates are removed automatically.
            </p>
          </div>

          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-600">
            Detected: {bulkIds.length} / {MAX_BULK}
          </span>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                Paste here
              </label>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={9}
                disabled={isRunning}
                className="mt-2 w-full resize-y rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D2042D]/25"
                placeholder={`Examples:\n1005008252021087\nhttps://www.aliexpress.com/item/1005008252021087.html\n\nMax ${MAX_BULK} per run.`}
              />

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={isRunning}
                  onClick={() => setBulkText("")}
                  className={clsx(
                    "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium focus:outline-none focus-visible:ring-2",
                    isRunning
                      ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 focus-visible:ring-[#D2042D]/30"
                  )}>
                  <Icons.Eraser size={14} className="text-slate-600" aria-hidden="true" />
                  Clear
                </button>

                <button
                  type="button"
                  disabled={isRunning}
                  onClick={() =>
                    setBulkText((t) =>
                      (t ? `${t}\n` : "") + "https://www.aliexpress.com/item/1005008252021087.html"
                    )
                  }
                  className={clsx(
                    "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium focus:outline-none focus-visible:ring-2",
                    isRunning
                      ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 focus-visible:ring-[#D2042D]/30"
                  )}>
                  <Icons.Plus size={14} className="text-slate-600" aria-hidden="true" />
                  Add sample
                </button>

                <div className="ml-auto text-[11px] text-slate-500">Tip: newline, comma, or spaces.</div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Behavior</p>

                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  <div className="flex items-start gap-2">
                    <Icons.CheckCircle2 size={14} className="text-emerald-600 mt-0.5" aria-hidden="true" />
                    <p>
                      Runs <span className="font-medium text-slate-900">Fetch -> Save</span> with live server status.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icons.Server size={14} className="text-slate-600 mt-0.5" aria-hidden="true" />
                    <p>
                      Real per-item progress and transfer metrics stream from backend.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icons.SkipForward size={14} className="text-amber-600 mt-0.5" aria-hidden="true" />
                    <p>
                      Existing products are skipped to avoid duplicates.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icons.Layers size={14} className="text-slate-600 mt-0.5" aria-hidden="true" />
                    <p>
                      Batch limit: <span className="font-medium text-slate-900">{MAX_BULK}</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icons.Server size={14} className="text-slate-600 mt-0.5" aria-hidden="true" />
                    <p>
                      Product get API cap: <span className="font-medium text-slate-900">{PRODUCT_GET_QPS}</span> calls/sec.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icons.Server size={14} className="text-slate-600 mt-0.5" aria-hidden="true" />
                    <p>
                      API workers per product: <span className="font-medium text-slate-900">{DEFAULT_WORKERS}</span>
                      <span className="text-slate-500"> (max {MAX_WORKERS})</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icons.PackageCheck size={14} className="text-slate-600 mt-0.5" aria-hidden="true" />
                    <p>
                      Product concurrency: <span className="font-medium text-slate-900">{PRODUCT_CONCURRENCY}</span>
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={startBulkImport}
                  disabled={isRunning}
                  className={clsx(
                    "mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-xs font-semibold focus:outline-none focus-visible:ring-2",
                    isRunning
                      ? "bg-white/60 border border-slate-200 text-slate-500 cursor-not-allowed"
                      : "bg-[#D2042D] text-white hover:bg-[#b40326] focus-visible:ring-[#D2042D]/30"
                  )}>
                  {isRunning ? (
                    <>
                      <Icons.Loader2 size={14} className="animate-spin" aria-hidden="true" />
                      Import running...
                    </>
                  ) : (
                    <>
                      <Icons.UploadCloud size={14} aria-hidden="true" />
                      Start bulk import
                    </>
                  )}
                </button>

                <p className="mt-3 text-[11px] text-slate-500">
                  Backend import may take time for large batches.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Active import jobs</h3>
            <p className="text-xs text-slate-500">
              Auto-refreshes live jobs from every admin session. Open Live view to stream the current job.
            </p>
          </div>

          <button
            type="button"
            onClick={loadActiveBulkJobs}
            disabled={activeJobsLoading}
            className={clsx(
              "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium focus:outline-none focus-visible:ring-2",
              activeJobsLoading
                ? "border-slate-200 bg-slate-100 text-slate-500 cursor-wait"
                : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 focus-visible:ring-[#D2042D]/30"
            )}>
            <Icons.RefreshCw size={14} className={activeJobsLoading ? "animate-spin" : ""} aria-hidden="true" />
            Refresh now
          </button>
        </div>

        {!activeJobs.length ? (
          <div className="px-6 py-5 text-sm text-slate-500">
            {activeJobsLoading ? "Loading active import jobs..." : "No queued or running bulk import jobs."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {activeJobs.map((job) => {
              const jobId = String(job?._id || "");
              const total = Number(job?.summary?.total || 0);
              const processed = Number(job?.summary?.processed || 0);
              const countries = Array.isArray(job?.countryCodes) ? job.countryCodes.length : 0;
              const createdAt = job?.createdAt ? new Date(job.createdAt).toLocaleString() : "-";
              const cancellingThisJob = activeJobCancelId === jobId;

              return (
                <div key={jobId} className="px-6 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900 truncate">Job #{jobId}</p>
                      <StatusPill state={mapServerStatus(job?.status)} />
                      {job?.cancelRequested ? (
                        <span className="inline-flex items-center rounded-full border border-rose-100 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
                          cancel requested
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {processed}/{total} processed - {countries} countries - {job?.workers || DEFAULT_WORKERS} API workers - created {createdAt}
                    </p>
                    {job?.error ? <p className="mt-1 text-xs text-rose-600 truncate">{job.error}</p> : null}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => watchBulkImportJob(job)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D2042D]/30">
                      <Icons.Radio size={14} aria-hidden="true" />
                      Live view
                    </button>

                    <button
                      type="button"
                      onClick={() => cancelBulkImport(jobId)}
                      disabled={Boolean(job?.cancelRequested) || Boolean(activeJobCancelId)}
                      className={clsx(
                        "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium focus:outline-none focus-visible:ring-2",
                        Boolean(job?.cancelRequested) || Boolean(activeJobCancelId)
                          ? "border-rose-100 bg-rose-50 text-rose-400 cursor-not-allowed"
                          : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50 focus-visible:ring-rose-200"
                      )}>
                      {cancellingThisJob ? (
                        <Icons.Loader2 size={14} className="animate-spin" aria-hidden="true" />
                      ) : (
                        <Icons.StopCircle size={14} aria-hidden="true" />
                      )}
                      {cancellingThisJob ? "Cancelling..." : "Cancel"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {isOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeModalIfSafe} />

          <div className="relative w-[min(1100px,95vw)] max-h-[90vh] bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Bulk import progress</h3>
                <p className="text-xs text-slate-500">
                  Job {activeJobId ? `#${activeJobId}` : "-"} - Completed {stats.done}/{stats.total || 0} - Success {stats.ok} - Skipped {stats.skipped} - Failed {stats.fail} - Running {stats.running}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {isRunning && activeJobId ? (
                  <button
                    type="button"
                    onClick={() => cancelBulkImport()}
                    disabled={isCancelling}
                    className={clsx(
                      "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium focus:outline-none focus-visible:ring-2",
                      isCancelling
                        ? "border-rose-100 bg-rose-50 text-rose-400 cursor-wait"
                        : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50 focus-visible:ring-rose-200"
                    )}>
                    {isCancelling ? (
                      <Icons.Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    ) : (
                      <Icons.StopCircle size={14} aria-hidden="true" />
                    )}
                    {isCancelling ? "Cancelling..." : "Cancel job"}
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={closeModalIfSafe}
                  disabled={isRunning}
                  className={clsx(
                    "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium focus:outline-none focus-visible:ring-2",
                    isRunning
                      ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 focus-visible:ring-[#D2042D]/30"
                  )}>
                  Close
                  <Icons.X size={14} className="text-slate-600" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="px-6 pt-4">
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className="h-2 bg-[#D2042D] rounded-full transition-all" style={{ width: `${stats.pct}%` }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                <span>{stats.pct}%</span>
                <span className={clsx("inline-flex items-center gap-1.5 font-medium", streamMeta.tone)}>
                  <StreamIcon
                    size={12}
                    className={clsx(streamMeta.pulse && "animate-pulse")}
                    aria-hidden="true"
                  />
                  {streamMeta.label}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 text-xs">
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">API window</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {Number(apiRateLimit?.startedInCurrentWindow || 0)}/{apiWindowLimit}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Queued</p>
                  <p className="mt-1 font-semibold text-slate-900">{Number(apiRateLimit?.queued || 0)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Cooldown</p>
                  <p className={clsx("mt-1 font-semibold", apiRateLimit?.paused ? "text-amber-700" : "text-slate-900")}>
                    {apiRateLimit?.paused ? formatPauseMs(apiRateLimit.pauseRemainingMs) : "None"}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Cron</p>
                  <p className={clsx("mt-1 font-semibold", stockCronMeta.pausedByBulkImport ? "text-amber-700" : "text-slate-900")}>
                    {stockCronMeta.label}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Responses</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {transfer.apiResponses}/{transfer.apiRequests}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Bandwidth</p>
                  <p className="mt-1 font-semibold text-slate-900">{formatRate(transfer.bytesPerSecond)}</p>
                </div>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-auto max-h-[72vh]">
              <section className="lg:col-span-2 rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">Items</h4>
                    <p className="text-xs text-slate-500">Real per-product status.</p>
                  </div>

                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-600">
                    {stats.total > jobs.length ? `Showing ${jobs.length}/${stats.total}` : `Batch ${stats.total || 0}`}
                  </span>
                </div>

                <div>
                  {!jobs.length ? (
                    <div className="p-8 text-center text-sm text-slate-500">
                      {isHydratingJob ? (
                        <span className="inline-flex items-center justify-center gap-2">
                          <Icons.Loader2 size={16} className="animate-spin text-slate-500" aria-hidden="true" />
                          Loading live job snapshot...
                        </span>
                      ) : activeJobId ? (
                        "Waiting for the first live backend snapshot..."
                      ) : (
                        "No items loaded yet."
                      )}
                    </div>
                  ) : (
                    jobs.map((j) => (
                      <div
                        key={j.id}
                        className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-11 w-11 rounded-xl border border-slate-200 bg-white flex items-center justify-center">
                            {j.state === "success" ? (
                              <Icons.CheckCircle2 size={18} className="text-emerald-600" aria-hidden="true" />
                            ) : j.state === "failed" ? (
                              <Icons.AlertTriangle size={18} className="text-rose-600" aria-hidden="true" />
                            ) : j.state === "exists" ? (
                              <Icons.SkipForward size={18} className="text-amber-600" aria-hidden="true" />
                            ) : j.state === "saving" ? (
                              <Icons.Save size={18} className="text-sky-600" aria-hidden="true" />
                            ) : j.state === "cancelled" ? (
                              <Icons.MinusCircle size={18} className="text-slate-500" aria-hidden="true" />
                            ) : (
                              <Icons.Clock3 size={18} className="text-slate-500" aria-hidden="true" />
                            )}
                          </div>

                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {j.id}
                              {j.attempt ? (
                                <span className="ml-2 text-[11px] font-medium text-slate-500">
                                  attempt {j.attempt}
                                </span>
                              ) : null}
                            </p>
                            <p className="mt-0.5 text-xs text-slate-500 truncate">{j.message}</p>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <StatusPill state={j.state} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <aside className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">Stream</h4>
                    <p className="text-xs text-slate-500">Backend job event log.</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setLogs([])}
                    disabled={isRunning}
                    className={clsx(
                      "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium focus:outline-none focus-visible:ring-2",
                      isRunning
                        ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                        : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 focus-visible:ring-[#D2042D]/30"
                    )}>
                    <Icons.Trash2 size={14} className="text-slate-600" aria-hidden="true" />
                    Clear
                  </button>
                </div>

                <div className="p-4">
                  <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-[11px] text-slate-500">Request size</p>
                      <p className="mt-1 font-semibold text-slate-900">{formatBytes(transfer.requestBytes)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-[11px] text-slate-500">Response size</p>
                      <p className="mt-1 font-semibold text-slate-900">{formatBytes(transfer.responseBytes)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-[11px] text-slate-500">Total data</p>
                      <p className="mt-1 font-semibold text-slate-900">{formatBytes(transfer.totalBytes)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-[11px] text-slate-500">Cron run</p>
                      <p className="mt-1 font-semibold text-slate-900">
                        {stockCronMeta.totalRows
                          ? `${stockCronMeta.processedRows}/${stockCronMeta.totalRows}`
                          : stockCronMeta.runCapLabel}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <pre className="font-mono text-[11px] leading-relaxed text-slate-700 whitespace-pre-wrap max-h-[420px] overflow-auto">
                      {logs.length ? logs.join("\n") : "Waiting for events..."}
                    </pre>
                  </div>
                </div>
              </aside>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Backend bulk import runs with {DEFAULT_WORKERS} API workers and one product at a time.
              </p>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600">
                Max batch: {MAX_BULK}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
